import { FC, useState, MouseEvent } from 'react';
import { FiltersModal as FiltersModalUI } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import { FiltersButton } from './FiltersButton';

import { FiltersContainer } from './Filters.styles';
import { Filters } from './Filters';
import useIsIE11 from '../../shared/hooks/app/useIsIE11';
import { LayoutVersion } from '../../constants';

const KEYPRESS_ENTER_EVENT = 0;

type TrackingEvents = 'options' | 'filters';
type TrackingEventsClickOption = 'open' | 'close' | 'update';

const triggerGATracking = (
    section: TrackingEvents,
    options: TrackingEventsClickOption
) => {
    const eventCategory =
        section === 'options' ? 'search results' : 'search_filters';
    const eventLabel = `${section}_${options}`;

    return triggerTracking({
        event: 'GAEvent',
        eventAction: 'click',
        eventCategory,
        eventLabel
    });
};

export type FiltersModalProps = {
    version: LayoutVersion;
    testId:
        | 'search-results-header_filters-button'
        | 'mobile-sub-header_filters-button';
};

export const FiltersModal: FC<FiltersModalProps> = ({ version }) => {
    const [showModal, setShowModal] = useState(false);
    const isIE11 = useIsIE11();

    const closeModal = () => {
        setShowModal(false);
        triggerGATracking('filters' as TrackingEvents, 'close');
        document.body.style.overflow = '';
    };

    const closeModalAfterUpdate = () => {
        setShowModal(false);
        triggerGATracking('filters' as TrackingEvents, 'update');
        document.body.style.overflow = '';
    };

    const openModal = (evt: MouseEvent<HTMLButtonElement>) => {
        //Having to do this as evt.detail is always 0 in IE11 and never show the modal :-(
        if (!isIE11) {
            // https://github.com/facebook/react/issues/3907#issuecomment-363948471
            if (evt.detail !== KEYPRESS_ENTER_EVENT) {
                setShowModal(true);
                triggerGATracking('filters' as TrackingEvents, 'open');
                document.body.style.overflow = 'hidden';
            }
        } else {
            setShowModal(true);
            triggerGATracking('filters' as TrackingEvents, 'open');
            document.body.style.overflow = 'hidden';
        }
    };

    return (
        <>
            <FiltersButton
                version={version}
                onClick={(evt) => openModal(evt)}
            />
            <FiltersModalUI isShowing={showModal} close={closeModal}>
                <FiltersContainer>
                    <Filters
                        closeModal={closeModal}
                        closeModalAfterUpdate={closeModalAfterUpdate}
                    />
                </FiltersContainer>
            </FiltersModalUI>
        </>
    );
};
